/**
 * Money and rates (CLAUDE.md rule 2).
 *
 * - Money is integer paise. In TypeScript it is a branded `bigint` (`Paise`);
 *   in Postgres a `bigint` column with a `_paise` suffix; in JSON a plain
 *   number that must be a safe integer (`"totalPaise": 45000`).
 * - Rates are integer basis points (`18% = 1800`), `_bp` columns.
 * - Nothing in this file divides, takes a percentage or rounds. That is
 *   pricing logic and belongs to `packages/pricing` only. Every helper here
 *   maps integers to integers, so no float can ever be produced.
 * - Quantities (`numeric(14,3)` in the DB) are deliberately not modelled here.
 *   When they are, use integer milli-units or a decimal string, never a float.
 */
import { z } from 'zod';
import type { Brand } from './brand.js';

/** An amount of money in integer paise (1 rupee = 100 paise). May be negative (refunds, reversals). */
export type Paise = Brand<bigint, 'Paise'>;

/** An integer rate in basis points (1% = 100 bp). */
export type BasisPoints = Brand<number, 'BasisPoints'>;

/** Range of a Postgres `bigint`, the column type every `_paise` column uses. */
export const PAISE_MIN = -(2n ** 63n);
export const PAISE_MAX = 2n ** 63n - 1n;

const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);
const MIN_SAFE = BigInt(Number.MIN_SAFE_INTEGER);

function checkedPaise(value: bigint): Paise {
  if (value < PAISE_MIN || value > PAISE_MAX) {
    throw new RangeError(`Paise value ${value} is outside the Postgres bigint range`);
  }
  return value as Paise;
}

/**
 * Make a `Paise` from an integer. Throws on floats, NaN, Infinity, unsafe
 * integers and values a Postgres `bigint` cannot hold.
 */
export function paise(value: number | bigint): Paise {
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) {
      throw new RangeError(`Paise must be a safe integer, got ${value}`);
    }
    return BigInt(value) as Paise;
  }
  return checkedPaise(value);
}

export const ZERO_PAISE: Paise = 0n as Paise;

export function addPaise(a: Paise, b: Paise): Paise {
  return checkedPaise(a + b);
}

export function subPaise(a: Paise, b: Paise): Paise {
  return checkedPaise(a - b);
}

export function sumPaise(values: Iterable<Paise>): Paise {
  let total = 0n;
  for (const v of values) total += v;
  return checkedPaise(total);
}

/** Multiply by an integer (e.g. a unit price by a whole count). Throws on a non-integer factor. */
export function mulPaiseByInt(amount: Paise, factor: number | bigint): Paise {
  if (typeof factor === 'number' && !Number.isSafeInteger(factor)) {
    throw new RangeError(`Factor must be a safe integer, got ${factor}`);
  }
  return checkedPaise(amount * BigInt(factor));
}

export function negatePaise(amount: Paise): Paise {
  return checkedPaise(-amount);
}

export function absPaise(amount: Paise): Paise {
  return amount < 0n ? negatePaise(amount) : amount;
}

/** -1, 0 or 1, usable as an `Array.prototype.sort` comparator. */
export function comparePaise(a: Paise, b: Paise): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0;
}

export const eqPaise = (a: Paise, b: Paise): boolean => a === b;
export const ltPaise = (a: Paise, b: Paise): boolean => a < b;
export const ltePaise = (a: Paise, b: Paise): boolean => a <= b;
export const gtPaise = (a: Paise, b: Paise): boolean => a > b;
export const gtePaise = (a: Paise, b: Paise): boolean => a >= b;
export const isZeroPaise = (a: Paise): boolean => a === 0n;
export const isNegativePaise = (a: Paise): boolean => a < 0n;
export const minPaise = (a: Paise, b: Paise): Paise => (a <= b ? a : b);
export const maxPaise = (a: Paise, b: Paise): Paise => (a >= b ? a : b);

/**
 * Serialise to the JSON wire form (a number). Throws if the value is outside
 * `Number.MIN_SAFE_INTEGER..Number.MAX_SAFE_INTEGER` (about ±₹90 lakh crore),
 * rather than silently losing precision.
 */
export function paiseToJson(amount: Paise): number {
  if (amount > MAX_SAFE || amount < MIN_SAFE) {
    throw new RangeError(`Paise value ${amount} cannot be represented exactly as a JSON number`);
  }
  return Number(amount);
}

/**
 * Money on the JSON wire: a safe integer number of paise. `z.int()` is bounded
 * to `Number.MIN_SAFE_INTEGER..Number.MAX_SAFE_INTEGER` and becomes
 * `{ type: 'integer' }` in JSON Schema.
 */
export const paiseJsonSchema = z.int({ message: 'Expected a safe integer number of paise' });

/**
 * Money in the domain: a `Paise` bigint within the Postgres `bigint` range.
 * Use it for values that are already bigints (the pg driver, Drizzle
 * `bigint` columns). It never appears in an API or event schema.
 */
export const paiseBigintSchema = z
  .bigint()
  .min(PAISE_MIN, { message: 'Paise outside the Postgres bigint range' })
  .max(PAISE_MAX, { message: 'Paise outside the Postgres bigint range' })
  .brand<'Paise'>();

function paiseCodec(wire: z.ZodInt) {
  return z.codec(wire, paiseBigintSchema, {
    decode: (n) => BigInt(n),
    // Zod hands encode the bigint before branding; it is range-checked already.
    encode: (p) => paiseToJson(p as Paise),
  });
}

/**
 * Money in API and event schemas: a codec between the wire number and the
 * domain `Paise`.
 *
 * - `parse` / `z.decode`: JSON number -> `Paise`. Rejects floats, NaN,
 *   Infinity, unsafe integers, strings and bigints.
 * - `z.encode`: `Paise` -> JSON number (throws `RangeError` past the
 *   safe-integer range), so a parsed response or event round-trips through
 *   `JSON.stringify(z.encode(schema, value))`.
 * - JSON Schema / OpenAPI: generate with `io: 'input'`, which describes the
 *   wire form for both requests and (encoded) responses. `io: 'output'` is
 *   the in-memory bigint and is deliberately not representable.
 *
 * Negative amounts are allowed; use `nonNegativePaiseSchema` where they are not.
 */
export const paiseSchema = paiseCodec(paiseJsonSchema);

export const nonNegativePaiseSchema = paiseCodec(
  z.int({ message: 'Expected a safe integer number of paise' }).min(0, {
    message: 'Expected a non-negative amount of paise',
  }),
);

/**
 * Upper bound for any basis-point rate: 100_000 bp = 1000%. Real rates are
 * far below this (GST 28% = 2800 bp, state liquor excise can exceed 100%);
 * the cap exists to catch a rupee or paise amount passed where a rate was
 * meant. Use `percentBasisPointsSchema` for rates that are a share of a
 * whole (discounts), which cannot exceed 100%.
 */
export const BASIS_POINTS_MAX = 100_000;

/** 100% in basis points. */
export const BASIS_POINTS_WHOLE = 10_000;

export function basisPoints(value: number): BasisPoints {
  if (!Number.isSafeInteger(value) || value < 0 || value > BASIS_POINTS_MAX) {
    throw new RangeError(`Basis points must be an integer in 0..${BASIS_POINTS_MAX}, got ${value}`);
  }
  return value as BasisPoints;
}

export const basisPointsSchema = z.int().min(0).max(BASIS_POINTS_MAX).brand<'BasisPoints'>();

/** A rate that is a share of a whole: 0..10_000 bp (0..100%). */
export const percentBasisPointsSchema = z
  .int()
  .min(0)
  .max(BASIS_POINTS_WHOLE)
  .brand<'BasisPoints'>();
